import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authApi } from '@/features/auth/api/auth.api'
import { useAuthStore } from '@/store/authStore'

const ME_QUERY_KEY = ['auth', 'me'] as const

/**
 * Drives the app-boot hydration: while this is pending, the router should
 * show a loading state rather than redirecting to /login — a 401 here is
 * "not signed in", not an error to surface to the user.
 */
export function useMeQuery() {
  const setSession = useAuthStore((s) => s.setSession)
  const clearSession = useAuthStore((s) => s.clearSession)

  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      try {
        const user = await authApi.me()
        setSession(user)
        return user
      } catch (error) {
        clearSession()
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useRegisterStudent() {
  return useMutation({ mutationFn: authApi.registerStudent })
}

export function useRegisterRecruiter() {
  return useMutation({ mutationFn: authApi.registerRecruiter })
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (result) => {
      if (result.user) {
        setSession(result.user)
        queryClient.setQueryData(ME_QUERY_KEY, result.user)
      }
    },
  })
}

export function useTwoFactorVerifyLogin() {
  const setSession = useAuthStore((s) => s.setSession)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.twoFactorVerifyLogin,
    onSuccess: (result) => {
      setSession(result.user)
      queryClient.setQueryData(ME_QUERY_KEY, result.user)
    },
  })
}

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      clearSession()
      queryClient.setQueryData(ME_QUERY_KEY, null)
    },
  })
}

export function useVerifyEmail() {
  return useMutation({ mutationFn: authApi.verifyEmail })
}

export function useResendVerification() {
  return useMutation({ mutationFn: authApi.resendVerification })
}

export function useForgotPassword() {
  return useMutation({ mutationFn: authApi.forgotPassword })
}

export function useResetPassword() {
  return useMutation({ mutationFn: authApi.resetPassword })
}

export function useSetupAccount() {
  return useMutation({ mutationFn: authApi.setupAccount })
}

export function useConfirmPassword() {
  return useMutation({ mutationFn: authApi.confirmPassword })
}

export function useTwoFactorSetup() {
  return useMutation({ mutationFn: authApi.twoFactorSetup })
}

/**
 * Fire-once-on-mount variant of the above, for TwoFactorSetupPage. A plain
 * useEffect-triggered mutation is fragile under StrictMode's dev-mode
 * double-render (this endpoint isn't idempotent — it overwrites the stored
 * secret on every call, so a duplicate/orphaned call can show a QR code for
 * a secret that's no longer the one actually saved server-side). useQuery's
 * built-in request deduplication — the same mechanism useMeQuery already
 * relies on above — avoids that without any manual guard.
 */
export function useTwoFactorSetupQuery() {
  return useQuery({
    queryKey: ['auth', 'two-factor-setup'],
    queryFn: authApi.twoFactorSetup,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useTwoFactorConfirm() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.twoFactorConfirm,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}
